/**
 * ChatInput — Text input area with bottom toolbar.
 *
 * Layout: file chips -> textarea -> toolbar (plus menu | feedback | agent | mic | send)
 * Send button is the last element in the DOM for E2E selector compatibility.
 */

import React from "react";
import { AgentSelector } from "./AgentSelector";
import { ChatInputEndedState } from "./ChatInputEndedState";
import { ChatFileChips } from "./ChatFileChips";
import { ChatTextRow } from "./ChatTextRow";
import { PlusMenu } from "./PlusMenu";
import { FeedbackButtons } from "./FeedbackButtons";
import AudioToggle from "./AudioRecorder/AudioToggle";
import AudioRecorder from "./AudioRecorder";
import { ArrowUp, X } from "lucide-react";

const ChatInput = ({
  retrievals, selectedRetrieval, currentRetrievalDescription,
  assistantColors, maxMsgLength, allowAsk, matrixModeAvailable,
  audioAvailable, onRetrievalChange, envRetrieval, isEmbedded, hasFixedRetrieval, retrievalId,
  inputValue, onInputChange, isLoading, onSend, chatId, question,
  onStartNewChat, ended, endedStatus, customErrorMessage,
  activeMatrix, activatingMatrix,
  realtimeError, getErrorMessage,
  addFeedback, addFeedback2,
  audioRecorder, isConnected, isTranscribingAudio, transcribingDots,
  realtimeEnabled, realtimeModeEnabled, onModeChange, realtimeConfig,
  allowFiles, allowMetadata, messageFiles,
  onAttachmentsOpen, onMetadataOpen, onMatrixOpen, onFileDelete,
  streamingAvailable, textareaRef,
}) => {
  const isOverLimit = inputValue.length > maxMsgLength;
  const isSendDisabled =
    isLoading || activatingMatrix || isOverLimit || allowAsk === false || isConnected;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.shiftKey) return;
    if (e.key === "Enter") {
      e.preventDefault();
      if (inputValue.trim() !== "" && !isOverLimit && !isSendDisabled) onSend();
    }
  };

  const primaryColor = assistantColors?.primaryColor || "black";

  if (retrievalId && ended) {
    return (
      <ChatInputEndedState
        assistantColors={assistantColors}
        activeMatrix={activeMatrix}
        endedStatus={endedStatus}
        realtimeError={realtimeError}
        customErrorMessage={customErrorMessage}
        getErrorMessage={getErrorMessage}
        onStartNewChat={onStartNewChat}
        plusMenu={
          <PlusMenu
            allowFiles={false}
            allowMetadata={false}
            matrixModeAvailable={false}
            activeMatrix={false}
            chatId={chatId}
            retrievalId={retrievalId}
            activatingMatrix={false}
            disabled={false}
            onStartNewChat={onStartNewChat}
            feedbackDisabled={true}
            isEmbedded={isEmbedded}
          />
        }
      />
    );
  }

  return (
    <div
      className="border border-gray-300 rounded-xl bg-white transition-all"
      style={{ "--agent-color": primaryColor }}
      onFocus={(e) => { e.currentTarget.style.borderColor = primaryColor; }}
      onBlur={(e) => { e.currentTarget.style.borderColor = ""; }}
    >
      {/* File chips — above textarea */}
      <ChatFileChips
        messageFiles={messageFiles}
        allowFiles={allowFiles}
        onFileDelete={onFileDelete}
        onAttachmentsOpen={onAttachmentsOpen}
        secondaryColor={assistantColors?.secondaryColor}
      />

      {/* Textarea + char counter */}
      <ChatTextRow
        textareaRef={textareaRef}
        inputValue={inputValue}
        onInputChange={onInputChange}
        onKeyDown={handleKeyDown}
        isOverLimit={isOverLimit}
        maxMsgLength={maxMsgLength}
        primaryColor={primaryColor}
        audioRecorder={audioRecorder}
        isConnected={isConnected}
        isTranscribingAudio={isTranscribingAudio}
        transcribingDots={transcribingDots}
        isLoading={isLoading}
        activatingMatrix={activatingMatrix}
        allowAsk={allowAsk}
      />

      {/* Bottom toolbar */}
      <div className="flex items-center justify-between px-2.5 pb-2.5 pt-1">
        {/* Left: Plus menu */}
        <div className="flex items-center gap-1">
          <PlusMenu
            allowFiles={allowFiles}
            allowMetadata={allowMetadata}
            matrixModeAvailable={matrixModeAvailable}
            activeMatrix={activeMatrix}
            chatId={chatId}
            retrievalId={retrievalId}
            activatingMatrix={activatingMatrix}
            disabled={!retrievalId}
            onStartNewChat={retrievalId && chatId ? onStartNewChat : undefined}
            onAttachmentsOpen={onAttachmentsOpen}
            onMetadataOpen={onMetadataOpen}
            onMatrixOpen={onMatrixOpen}
            feedbackDisabled={!retrievalId || !chatId}
            isEmbedded={isEmbedded}
          />
        </div>

        {/* Right: Feedback | Agent selector | Audio | Send */}
        <div className="flex items-center gap-1">
          {/* Feedback — hidden on mobile */}
          <div className="hidden sm:flex items-center">
            {!isEmbedded && (
              <FeedbackButtons
                chatId={chatId}
                questionId={question?.id}
                addFeedback={addFeedback}
                addFeedback2={addFeedback2}
                disabled={!retrievalId || !chatId}
                assistantColors={assistantColors}
                retrievalId={retrievalId}
                selectedRetrieval={selectedRetrieval}
              />
            )}
          </div>

          {/* Separator */}
          <div className="hidden sm:block w-px h-5 bg-gray-200 mx-1" />

          {/* Agent selector - toolbar pill. Grayed out (visible, not interactive) when the agent is fixed by the route (/projects/:id). */}
          <AgentSelector
            retrievals={retrievals}
            selectedRetrieval={selectedRetrieval}
            currentDescription={currentRetrievalDescription}
            agentColor={assistantColors?.primaryColor}
            onChange={onRetrievalChange}
            disabled={envRetrieval || !retrievalId || hasFixedRetrieval}
            variant="toolbar"
          />
          {/* Audio recorder (inline mic) */}
          <AudioRecorder
            disabled={isLoading || activatingMatrix || allowAsk === false || !retrievalId}
            assistantColors={assistantColors}
            instance={audioRecorder}
          />

          {/* Audio toggle (realtime) */}
          <AudioToggle
            show={true}
            disabled={isLoading || activatingMatrix || allowAsk === false || !retrievalId}
            assistantColors={assistantColors}
            realtimeAvailable={audioAvailable && realtimeEnabled}
            conversationStarted={question !== null}
            onConnect={realtimeConfig?.connectConversation}
            onDisconnect={realtimeConfig?.disconnectConversation}
            isConnected={isConnected}
            realtimeModeEnabled={realtimeModeEnabled}
            onModeChange={onModeChange}
          />

          {/* Send / Cancel — MUST be last button with SVG for E2E compat */}
          {audioRecorder?.isRecording ? (
            <button
              type="button"
              aria-label="Cancel recording"
              className="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 transition-colors border-none cursor-pointer bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900"
              onClick={() => audioRecorder.cancelRecording()}
            >
              <X size={16} />
            </button>
          ) : (
            <button
              type="button"
              aria-label="Send message"
              className="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 transition-colors border-none cursor-pointer disabled:opacity-35 disabled:cursor-default text-white"
              style={{ backgroundColor: isSendDisabled ? "#d1d5db" : primaryColor }}
              disabled={isSendDisabled}
              onClick={() => { if (!isConnected) onSend(); }}
            >
              <ArrowUp size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export { ChatInput };
